import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

import { AuthService } from '../../api-authorization/authorize.service';
import { InbetriebnahmeService } from '../inbetriebnahme.service';
import { NetworkStatusService, TimeoutFehler } from './network-status.service';
import { OfflineDbService } from './offline-db.service';
import { OfflineSyncService } from './offline-sync.service';

import {
  standardFelder,
  MessstelleDetail,
  MessstelleKategorie,
  MessstellePruefpunkt,
  MessstellePruefprotokoll,
  MessstellenListeDetail,
  MessstellenListeSummary,
  PruefpunktBewertung,
  PruefpunktErgebnis,
  PruefpunktId,
  PruefpunktStatus,
  Pruefergebnis,
  PruefprotokollTyp,
  SignalStatus,
  aktivitaetsGruppeVon,
  eindeutigerSpaltenName,
  findeSpalte,
  istLokaleId,
  neueLokaleId,
  neueUuid,
  normalizePruefergebnis,
  normalizePruefpunktErgebnis,
  normalizeSignalStatus,
  pruefergebnisText,
  pruefpunktErgebnisText,
  signalStatusText
} from '../../Models/messstelle.model';

import {
  DatenErgebnis,
  OfflineDetailEintrag,
  OfflineKategorie,
  OfflineListeEintrag,
  OfflineMessstelleDetail,
  SchreibErgebnis,
  SyncQueueEintrag,
  SyncTyp
} from '../../Models/offline.model';

/**
 * Der einzige Zugang der Inbetriebnahme-Komponenten zu ihren Daten.
 *
 * Die Komponenten fragen hier nach Messstellen und schreiben hier Prüfungen –
 * sie müssen NICHT wissen, ob gerade eine Verbindung besteht. Genau das ist
 * der Zweck dieser Schicht: ohne sie stünde in jeder Komponente ein
 * `if (online) … else …`, und jeder vergessene Zweig wäre ein Datenverlust
 * auf der Anlage.
 *
 * Lesen:  Server versuchen → bei Erfolg anzeigen UND lokal nachziehen;
 *         bei Netzwerkfehler oder Zeitüberschreitung aus der IndexedDB.
 *
 * Schreiben: Server versuchen → bei Erfolg lokal nachziehen;
 *         bei Netzwerkfehler lokal speichern und in die Warteschlange.
 *
 * Was NICHT zum Offline-Fallback führt: 400, 401, 403, 404. Das sind echte
 * Antworten des Servers. Eine fehlende Berechtigung als "offline" zu
 * behandeln würde dem Benutzer vorgaukeln, seine Änderung käme später schon
 * durch – sie käme nie durch.
 */
@Injectable({ providedIn: 'root' })
export class InbetriebnahmeDataService {

  /**
   * Zeitgrenze für lesende Zugriffe. Auf einer Anlage ist eine Anfrage, die
   * nach zwölf Sekunden noch läuft, praktisch tot – dann lieber sofort den
   * lokalen Bestand zeigen, als den Monteur warten zu lassen.
   */
  private static readonly LESE_TIMEOUT_MS = 12_000;

  /** Schreiben darf etwas länger dauern, bevor lokal gespeichert wird. */
  private static readonly SCHREIB_TIMEOUT_MS = 15_000;

  /**
   * Anzeige eines leeren Werts im Verlauf. Muss zu `Aktivitaetsprotokoll`
   * im Backend passen, sonst stünde offline etwas anderes als online.
   */
  private static readonly LEER_TEXT = '(leer)';

  /** Dasselbe für eine fehlende Kategorie bzw. einen fehlenden Anlagenteil. */
  private static readonly KEINE_ZUORDNUNG = 'Keine Zuordnung';

  /** Feldname der Notiz im Verlauf – wie `Aktivitaetsprotokoll.NotizFeldName`. */
  private static readonly NOTIZ_FELDNAME = 'Notiz zur Messstelle';

  constructor(
    private api: InbetriebnahmeService,
    private db: OfflineDbService,
    private network: NetworkStatusService,
    private sync: OfflineSyncService,
    private auth: AuthService
  ) { }

  // ===================================================================
  // LESEN
  // ===================================================================

  /**
   * Die Übersicht aller Inbetriebnahmen.
   *
   * Offline kommen nur die, die vorher heruntergeladen wurden – alles andere
   * gibt es lokal schlicht nicht.
   */
  getListen(): Observable<DatenErgebnis<MessstellenListeSummary[]>> {
    return from(this.ladeListen());
  }

  private async ladeListen(): Promise<DatenErgebnis<MessstellenListeSummary[]>> {
    if (this.network.lohntVersuch()) {
      try {
        const listen = await this.mitTimeout(
          this.api.getListen(),
          InbetriebnahmeDataService.LESE_TIMEOUT_MS
        );

        this.network.meldeErfolg();

        return { daten: listen ?? [], quelle: 'server' };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    const lokal = await this.db.ladeListen();

    return {
      daten: lokal.map(l => l.liste),
      quelle: 'offline',
      standUtc: this.juengsterStand(lokal)
    };
  }

  /** Eine Inbetriebnahme mit allen Messstellen. */
  getListe(id: number): Observable<DatenErgebnis<MessstellenListeDetail>> {
    return from(this.ladeListe(id));
  }

  private async ladeListe(id: number): Promise<DatenErgebnis<MessstellenListeDetail>> {
    const offlineVerfuegbar = await this.db.istOfflineVerfuegbar(id);

    if (offlineVerfuegbar) {
      // Beim Öffnen einer offline gespeicherten Inbetriebnahme ist ein guter
      // Moment, Liegengebliebenes loszuwerden.
      void this.sync.synchronisiere('geoeffnet');
    }

    if (this.network.lohntVersuch()) {
      try {
        const liste = await this.mitTimeout(
          this.api.getListe(id),
          InbetriebnahmeDataService.LESE_TIMEOUT_MS
        );

        this.network.meldeErfolg();

        // Lokale Kopie nachziehen – aber nur, wenn diese Inbetriebnahme
        // überhaupt offline gehalten wird. Sonst würde jeder Seitenaufruf
        // stillschweigend Daten auf dem Gerät ablegen.
        if (offlineVerfuegbar) {
          await this.aktualisiereLokaleListe(id, liste);
        }

        return { daten: liste, quelle: 'server' };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler) || !offlineVerfuegbar) {
          throw fehler;
        }
      }
    }

    const kopf = await this.db.ladeListe(id);

    if (!kopf) {
      throw new Error(
        'Diese Inbetriebnahme wurde nicht für die Offline-Nutzung heruntergeladen.'
      );
    }

    const zeilen = await this.db.ladeMessstellen(id);

    return {
      daten: {
        ...kopf.liste,
        messstellen: zeilen.map(z => z.data)
      } as MessstellenListeDetail,
      quelle: 'offline',
      standUtc: kopf.downloadedAtUtc
    };
  }

  /** Eine einzelne Messstelle mit allem, was die Detailseite braucht. */
  getMessstelle(id: number): Observable<DatenErgebnis<MessstelleDetail>> {
    return from(this.ladeMessstelle(id));
  }

  private async ladeMessstelle(id: number): Promise<DatenErgebnis<MessstelleDetail>> {
    const lokal = await this.db.ladeDetail(id);

    if (this.network.lohntVersuch()) {
      try {
        const detail = await this.mitTimeout(
          this.api.getMessstelle(id),
          InbetriebnahmeDataService.LESE_TIMEOUT_MS
        );

        this.network.meldeErfolg();

        if (lokal) {
          await this.aktualisiereLokalesDetail(detail);
        }

        return { daten: detail, quelle: 'server' };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler) || !lokal) {
          throw fehler;
        }
      }
    }

    if (!lokal) {
      throw new Error(
        'Diese Messstelle steht offline nicht zur Verfügung. '
        + 'Die Inbetriebnahme muss vorher offline verfügbar gemacht werden.'
      );
    }

    const kopf = await this.db.ladeListe(lokal.messstellenListeId);

    return {
      daten: this.hydriere(lokal.data, kopf),
      quelle: 'offline',
      standUtc: kopf?.downloadedAtUtc ?? null
    };
  }

  /** Die auswählbaren Kategorien. */
  getKategorien(): Observable<DatenErgebnis<MessstelleKategorie[]>> {
    return from(this.ladeKategorien());
  }

  private async ladeKategorien(): Promise<DatenErgebnis<MessstelleKategorie[]>> {
    if (this.network.lohntVersuch()) {
      try {
        const kategorien = await this.mitTimeout(
          this.api.getKategorien(),
          InbetriebnahmeDataService.LESE_TIMEOUT_MS
        );

        this.network.meldeErfolg();

        return { daten: kategorien ?? [], quelle: 'server' };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    const lokal = await this.db.ladeKategorien();

    return {
      daten: lokal.map(k => this.alsKategorie(k.data)),
      quelle: 'offline'
    };
  }

  // ===================================================================
  // SCHREIBEN
  // ===================================================================

  /**
   * Ergebnis und/oder Bemerkung eines Prüfpunkts.
   *
   * Der wichtigste Schreibvorgang der ganzen Anwendung: das ist der Griff,
   * den ein Monteur hundertmal am Tag betätigt, oft ohne Netz.
   */
  updatePruefpunkt(
    messstelle: MessstelleDetail,
    punkt: MessstellePruefpunkt,
    ergebnis: PruefpunktErgebnis,
    bemerkung: string | null
  ): Observable<SchreibErgebnis<MessstellePruefpunkt>> {
    return from(this.schreibePruefpunkt(messstelle, punkt, ergebnis, bemerkung));
  }

  private async schreibePruefpunkt(
    messstelle: MessstelleDetail,
    punkt: MessstellePruefpunkt,
    ergebnis: PruefpunktErgebnis,
    bemerkung: string | null
  ): Promise<SchreibErgebnis<MessstellePruefpunkt>> {

    // Ein offline angelegter Punkt hat noch keine Server-Id. Solange die
    // fehlt, kann nur lokal geschrieben werden – der Server wüsste nicht,
    // welchen Datensatz er meint.
    const nurLokal = istLokaleId(punkt.id);

    if (!nurLokal && this.network.lohntVersuch()) {
      try {
        const aktualisiert = await this.mitTimeout(
          this.api.updatePruefpunkt(punkt.id as number, ergebnis, bemerkung),
          InbetriebnahmeDataService.SCHREIB_TIMEOUT_MS
        );

        this.network.meldeErfolg();

        await this.uebernehmeServerPunkt(messstelle.id, aktualisiert);

        return { daten: aktualisiert, lokal: false };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    return this.lokalSchreiben(
      messstelle,
      'PRUEFPUNKT_ERGEBNIS',
      {
        entityId: nurLokal ? null : (punkt.id as number),
        clientEntityId: nurLokal ? this.clientKennung(punkt) : (punkt.clientId ?? null),

        /*
         * Bei einem Punkt, der offline aus einer Kategorie entstanden ist, ist
         * die Vorlage der einzige Anker: die Server-Id gibt es noch nicht, und
         * die lokale Kennung kennt der Server nicht – er legt den Punkt beim
         * Abgleich selbst aus derselben Vorlage an.
         */
        vorlageId: nurLokal ? (punkt.vorlageId ?? null) : null,

        baseVersion: punkt.version ?? null,
        payload: { ergebnis, bemerkung }
      },
      detail => {
        const ziel = (detail.pruefpunkte ?? []).find(p => p.id === punkt.id);

        if (!ziel) {
          throw new Error('Der Prüfpunkt ist im lokalen Bestand nicht vorhanden.');
        }

        const altesErgebnis = normalizePruefpunktErgebnis(ziel.ergebnis);
        const alteBemerkung = ziel.bemerkung ?? null;

        this.setzePunktErgebnis(ziel, ergebnis, bemerkung);
        ziel.nichtSynchronisiert = true;

        /*
         * Dieselbe Aufteilung wie serverseitig: eine Ergebnisänderung und eine
         * reine Bemerkungsänderung sind zwei verschiedene Vorgänge. Ändert
         * sich beides zusammen, entsteht nur der Eintrag zum Ergebnis – die
         * Bemerkung hängt dort mit drin.
         */
        if (altesErgebnis !== ergebnis) {
          detail.pruefprotokoll = [
            this.lokalerProtokolleintrag(
              detail.id,
              PruefprotokollTyp.PruefpunktErgebnis,
              ergebnis,
              altesErgebnis,
              bemerkung,
              ziel.id,
              ziel.bezeichnung
            ),
            ...(detail.pruefprotokoll ?? [])
          ];
        } else if ((alteBemerkung ?? '') !== (bemerkung ?? '')) {
          detail.pruefprotokoll = [
            this.lokalerWertEintrag(
              detail.id,
              PruefprotokollTyp.PruefpunktBemerkung,
              {
                altWert: alteBemerkung ?? '',
                neuWert: bemerkung ?? '',
                pruefpunktId: ziel.id,
                pruefpunktBezeichnung: ziel.bezeichnung,
                bemerkung,
                paar: true
              }
            ),
            ...(detail.pruefprotokoll ?? [])
          ];
        }

        return ziel;
      }
    );
  }

  /** Einen eigenen Prüfpunkt ergänzen. */
  createPruefpunkt(
    messstelle: MessstelleDetail,
    bezeichnung: string,
    section: string | null
  ): Observable<SchreibErgebnis<MessstellePruefpunkt>> {
    return from(this.schreibeNeuerPunkt(messstelle, bezeichnung, section));
  }

  private async schreibeNeuerPunkt(
    messstelle: MessstelleDetail,
    bezeichnung: string,
    section: string | null
  ): Promise<SchreibErgebnis<MessstellePruefpunkt>> {

    if (this.network.lohntVersuch()) {
      try {
        const punkt = await this.mitTimeout(
          this.api.createPruefpunkt(messstelle.id, bezeichnung, section),
          InbetriebnahmeDataService.SCHREIB_TIMEOUT_MS
        );

        this.network.meldeErfolg();

        await this.aendereLokalesDetail(messstelle.id, detail => {
          detail.pruefpunkte = [...(detail.pruefpunkte ?? []), punkt];
          return punkt;
        });

        return { daten: punkt, lokal: false };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    /*
     * Offline gibt es noch keine Id von der Datenbank. Der Punkt bekommt
     * deshalb eine Client-UUID; beim Abgleich legt der Server ihn unter
     * genau dieser Kennung an und meldet die echte Id zurück, worauf der
     * lokale Bestand nachgezogen wird.
     *
     * Die Kennung ist zugleich der Idempotenzschlüssel: ein zweites Mal
     * gesendet, entsteht KEIN zweiter Prüfpunkt.
     */
    const kennung = neueUuid();
    const lokaleId = `local:${kennung}`;

    return this.lokalSchreiben(
      messstelle,
      'PRUEFPUNKT_ANLEGEN',
      {
        clientId: kennung,
        entityId: null,
        clientEntityId: kennung,
        baseVersion: null,
        payload: { bezeichnung, section }
      },
      detail => {
        const abschnitt = (section ?? '').trim() || 'Zusätzliche Prüfpunkte';

        const maxSort = (detail.pruefpunkte ?? [])
          .filter(p => p.section === abschnitt)
          .reduce((max, p) => Math.max(max, p.sortOrder ?? 0), 0);

        const neu: MessstellePruefpunkt = {
          id: lokaleId,
          vorlageId: null,
          section: abschnitt,
          bezeichnung: bezeichnung.trim(),
          sortOrder: maxSort + 1,
          ergebnis: PruefpunktErgebnis.NichtGeprueft,
          status: PruefpunktStatus.Offen,
          bewertung: PruefpunktBewertung.Keine,
          bemerkung: null,
          quittiertAmUtc: null,
          quittiertVon: null,
          version: 0,
          clientId: kennung,
          nichtSynchronisiert: true
        };

        detail.pruefpunkte = [...(detail.pruefpunkte ?? []), neu];

        detail.pruefprotokoll = [
          this.lokalerWertEintrag(
            detail.id,
            PruefprotokollTyp.PruefpunktAngelegt,
            {
              neuWert: neu.bezeichnung,
              pruefpunktId: neu.id,
              pruefpunktBezeichnung: neu.bezeichnung,
              beschreibung: abschnitt ? `Bereich: ${abschnitt}` : null
            }
          ),
          ...(detail.pruefprotokoll ?? [])
        ];

        return neu;
      }
    );
  }

  deletePruefpunkt(
    messstelle: MessstelleDetail,
    punkt: MessstellePruefpunkt
  ): Observable<SchreibErgebnis<void>> {
    return from(this.schreibeLoeschung(messstelle, punkt));
  }

  private async schreibeLoeschung(
    messstelle: MessstelleDetail,
    punkt: MessstellePruefpunkt
  ): Promise<SchreibErgebnis<void>> {
    const nurLokal = istLokaleId(punkt.id);

    if (!nurLokal && this.network.lohntVersuch()) {
      try {
        await this.mitTimeout(
          this.api.deletePruefpunkt(punkt.id as number),
          InbetriebnahmeDataService.SCHREIB_TIMEOUT_MS
        );

        this.network.meldeErfolg();

        await this.aendereLokalesDetail(messstelle.id, detail => {
          detail.pruefpunkte = (detail.pruefpunkte ?? []).filter(p => p.id !== punkt.id);
          return undefined as any;
        });

        return { daten: undefined as void, lokal: false };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    /*
     * Ein Punkt, der nur lokal existiert und noch nie beim Server war, wird
     * einfach entfernt – zusammen mit seinen wartenden Änderungen. Ein
     * "Anlegen und wieder löschen" braucht der Server nie zu sehen.
     */
    if (nurLokal) {
      const kennung = this.clientKennung(punkt);

      // Der bereinigte Verlauf, wie er nach der Transaktion aussieht.
      let verlauf: MessstellePruefprotokoll[] = [];

      await this.db.db.transaction(
        'rw',
        this.db.db.details,
        this.db.db.syncQueue,
        async () => {
          const eintrag = await this.db.db.details.get(messstelle.id);

          if (eintrag) {
            eintrag.data.pruefpunkte =
              (eintrag.data.pruefpunkte ?? []).filter(p => p.id !== punkt.id);

            /*
             * Auch die lokalen Verlaufseinträge zu diesem Punkt verschwinden.
             * Der Server hat ihn nie gesehen – ein „angelegt" und ein
             * „gelöscht" zu einem Vorgang, den es nie gab, wären beide falsch.
             * Betroffen sind ausschliesslich noch nicht übertragene Einträge.
             */
            eintrag.data.pruefprotokoll =
              (eintrag.data.pruefprotokoll ?? []).filter(
                e => !(e.nichtSynchronisiert && e.pruefpunktId === punkt.id)
              );

            verlauf = eintrag.data.pruefprotokoll;

            await this.db.db.details.put(eintrag);
          }

          const wartend = await this.db.db.syncQueue
            .filter(e => e.clientEntityId === kennung)
            .toArray();

          await this.db.db.syncQueue.bulkDelete(wartend.map(e => e.id));
        }
      );

      await this.sync.aktualisiereStatus();

      return { daten: undefined as void, lokal: true, pruefprotokoll: verlauf };
    }

    const ergebnis = await this.lokalSchreiben(
      messstelle,
      'PRUEFPUNKT_LOESCHEN',
      {
        entityId: punkt.id as number,
        clientEntityId: punkt.clientId ?? null,
        baseVersion: punkt.version ?? null,
        payload: {}
      },
      detail => {
        detail.pruefpunkte = (detail.pruefpunkte ?? []).filter(p => p.id !== punkt.id);

        // Der Verlaufseintrag überlebt den Punkt – genau wie serverseitig.
        detail.pruefprotokoll = [
          this.lokalerWertEintrag(
            detail.id,
            PruefprotokollTyp.PruefpunktGeloescht,
            {
              neuWert: punkt.bezeichnung,
              pruefpunktId: punkt.id,
              pruefpunktBezeichnung: punkt.bezeichnung,
              beschreibung: this.loeschHinweis(punkt)
            }
          ),
          ...(detail.pruefprotokoll ?? [])
        ];

        return undefined as any;
      }
    );

    return {
      daten: undefined as void,
      lokal: ergebnis.lokal,
      pruefprotokoll: ergebnis.pruefprotokoll
    };
  }

  /** Gesamtergebnis der Messstelle. */
  setPruefergebnis(
    messstelle: MessstelleDetail,
    pruefergebnis: Pruefergebnis
  ): Observable<SchreibErgebnis<MessstelleDetail>> {
    return from(this.schreibePruefergebnis(messstelle, pruefergebnis));
  }

  private async schreibePruefergebnis(
    messstelle: MessstelleDetail,
    pruefergebnis: Pruefergebnis
  ): Promise<SchreibErgebnis<MessstelleDetail>> {

    if (this.network.lohntVersuch()) {
      try {
        const detail = await this.mitTimeout(
          this.api.setPruefergebnis(messstelle.id, pruefergebnis),
          InbetriebnahmeDataService.SCHREIB_TIMEOUT_MS
        );

        this.network.meldeErfolg();
        await this.aktualisiereLokalesDetail(detail);

        return { daten: detail, lokal: false };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    const ergebnis = await this.lokalSchreiben(
      messstelle,
      'MESSSTELLE_PRUEFERGEBNIS',
      {
        entityId: messstelle.id,
        clientEntityId: null,
        baseVersion: messstelle.version ?? null,
        payload: { pruefergebnis }
      },
      detail => {
        const altesErgebnis = normalizePruefergebnis(detail.pruefergebnis);

        detail.pruefergebnis = pruefergebnis;

        if (altesErgebnis !== pruefergebnis) {
          detail.pruefprotokoll = [
            this.lokalerProtokolleintrag(
              detail.id,
              PruefprotokollTyp.MessstellenErgebnis,
              pruefergebnis,
              altesErgebnis,
              null
            ),
            ...(detail.pruefprotokoll ?? [])
          ];
        }

        return undefined as any;
      }
    );

    return {
      daten: await this.lokalesDetailAlsMessstelle(messstelle.id, messstelle),
      lokal: ergebnis.lokal
    };
  }

  /**
   * Die allgemeine Notiz zur Messstelle. Leerer Text entfernt sie.
   *
   * Eigener Schreibweg neben dem Prüfergebnis: die Notiz ist freiwillig, und
   * ihr Verlaufseintrag soll auch offline als Notiz-Aktivität entstehen – mit
   * dem Zeitpunkt, zu dem sie tatsächlich getippt wurde.
   */
  setMessstelleNotiz(
    messstelle: MessstelleDetail,
    notiz: string | null
  ): Observable<SchreibErgebnis<MessstelleDetail>> {
    return from(this.schreibeNotiz(messstelle, notiz));
  }

  private async schreibeNotiz(
    messstelle: MessstelleDetail,
    notiz: string | null
  ): Promise<SchreibErgebnis<MessstelleDetail>> {

    const neueNotiz = (notiz ?? '').trim() ? notiz : null;

    if (this.network.lohntVersuch()) {
      try {
        const detail = await this.mitTimeout(
          this.api.setMessstelleNotiz(messstelle.id, neueNotiz),
          InbetriebnahmeDataService.SCHREIB_TIMEOUT_MS
        );

        this.network.meldeErfolg();
        await this.aktualisiereLokalesDetail(detail);

        return { daten: detail, lokal: false };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    const ergebnis = await this.lokalSchreiben(
      messstelle,
      'MESSSTELLE_NOTIZ',
      {
        entityId: messstelle.id,
        clientEntityId: null,

        /*
         * Ohne Basisversion: die Notiz hängt an keinem anderen Feld, und ein
         * Konflikt über einen freiwilligen Text wäre vor Ort nur im Weg.
         */
        baseVersion: null,
        payload: { notiz: neueNotiz }
      },
      detail => {
        const alteNotiz = detail.pruefergebnisBemerkung ?? null;

        if ((alteNotiz ?? '') !== (neueNotiz ?? '')) {
          detail.pruefergebnisBemerkung = neueNotiz;

          detail.pruefprotokoll = [
            this.lokalerWertEintrag(
              detail.id,
              PruefprotokollTyp.MessstelleNotiz,
              {
                feldName: InbetriebnahmeDataService.NOTIZ_FELDNAME,
                altWert: alteNotiz ?? '',
                neuWert: neueNotiz ?? '',
                paar: true
              }
            ),
            ...(detail.pruefprotokoll ?? [])
          ];
        }

        return undefined as any;
      }
    );

    return {
      daten: await this.lokalesDetailAlsMessstelle(messstelle.id, messstelle),
      lokal: ergebnis.lokal
    };
  }

  /** Workflow-Status der Messstelle. */
  setSignalStatus(
    messstelle: MessstelleDetail,
    signalStatus: SignalStatus,
    bemerkung: string | null = null
  ): Observable<SchreibErgebnis<MessstelleDetail>> {
    return from(this.schreibeSignalStatus(messstelle, signalStatus, bemerkung));
  }

  private async schreibeSignalStatus(
    messstelle: MessstelleDetail,
    signalStatus: SignalStatus,
    bemerkung: string | null
  ): Promise<SchreibErgebnis<MessstelleDetail>> {

    if (this.network.lohntVersuch()) {
      try {
        const detail = await this.mitTimeout(
          this.api.setSignalStatus(messstelle.id, signalStatus, bemerkung),
          InbetriebnahmeDataService.SCHREIB_TIMEOUT_MS
        );

        this.network.meldeErfolg();
        await this.aktualisiereLokalesDetail(detail);

        return { daten: detail, lokal: false };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    const ergebnis = await this.lokalSchreiben(
      messstelle,
      'MESSSTELLE_SIGNALSTATUS',
      {
        entityId: messstelle.id,
        clientEntityId: null,
        baseVersion: messstelle.version ?? null,
        payload: { signalStatus, bemerkung }
      },
      detail => {
        const alterStatus = normalizeSignalStatus(detail.signalStatus);

        if (alterStatus !== signalStatus) {
          detail.signalStatus = signalStatus;

          detail.pruefprotokoll = [
            this.lokalerProtokolleintrag(
              detail.id,
              PruefprotokollTyp.SignalStatus,
              signalStatus,
              alterStatus,
              bemerkung
            ),
            ...(detail.pruefprotokoll ?? [])
          ];
        }

        return undefined as any;
      }
    );

    return {
      daten: await this.lokalesDetailAlsMessstelle(messstelle.id, messstelle),
      lokal: ergebnis.lokal
    };
  }

  /**
   * Kategorie zuweisen oder entfernen.
   *
   * Offline werden die Prüfpunkte aus den mitgelieferten Vorlagen LOKAL
   * erzeugt – sonst stünde der Monteur vor einer leeren Prüfliste, bis er
   * wieder Netz hat. Beim Abgleich erzeugt der Server dieselben Punkte aus
   * derselben Kategorie; die lokalen werden dann durch den nächsten Download
   * ersetzt.
   */
  setKategorie(
    messstelle: MessstelleDetail,
    kategorieId: number | null
  ): Observable<SchreibErgebnis<MessstelleDetail>> {
    return from(this.schreibeKategorie(messstelle, kategorieId));
  }

  private async schreibeKategorie(
    messstelle: MessstelleDetail,
    kategorieId: number | null
  ): Promise<SchreibErgebnis<MessstelleDetail>> {

    if (this.network.lohntVersuch()) {
      try {
        const detail = await this.mitTimeout(
          this.api.setKategorie(messstelle.id, kategorieId),
          InbetriebnahmeDataService.SCHREIB_TIMEOUT_MS
        );

        this.network.meldeErfolg();
        await this.aktualisiereLokalesDetail(detail);

        return { daten: detail, lokal: false };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    const kategorien = await this.db.ladeKategorien();
    const kategorie = kategorien.find(k => k.id === kategorieId)?.data ?? null;

    if (kategorieId != null && !kategorie) {
      throw new Error(
        'Diese Kategorie steht offline nicht zur Verfügung. '
        + 'Sie wurde nach dem letzten Download angelegt.'
      );
    }

    const ergebnis = await this.lokalSchreiben(
      messstelle,
      'MESSSTELLE_KATEGORIE',
      {
        entityId: messstelle.id,
        clientEntityId: null,
        baseVersion: messstelle.version ?? null,
        payload: { kategorieId }
      },
      detail => {
        const alterName = detail.kategorieName ?? null;
        const alteId = detail.kategorieId ?? null;

        detail.kategorieId = kategorieId;
        detail.kategorieName = kategorie?.name ?? null;
        detail.pruefpunkte = this.baueVorlagenPunkte(detail.pruefpunkte ?? [], kategorie);

        if (alteId !== (kategorieId ?? null)) {
          detail.pruefprotokoll = [
            this.lokalerWertEintrag(
              detail.id,
              PruefprotokollTyp.Kategorie,
              {
                feldName: 'Kategorie',
                altWert: alterName ?? '',
                neuWert: detail.kategorieName ?? '',
                paar: true
              }
            ),
            ...(detail.pruefprotokoll ?? [])
          ];
        }

        return undefined as any;
      }
    );

    return {
      daten: await this.lokalesDetailAlsMessstelle(messstelle.id, messstelle),
      lokal: ergebnis.lokal
    };
  }

  /** Die bearbeiteten allgemeinen Daten. */
  updateWerte(
    messstelle: MessstelleDetail,
    werte: { [standardFeld: string]: string }
  ): Observable<SchreibErgebnis<MessstelleDetail>> {
    return from(this.schreibeWerte(messstelle, werte));
  }

  private async schreibeWerte(
    messstelle: MessstelleDetail,
    werte: { [standardFeld: string]: string }
  ): Promise<SchreibErgebnis<MessstelleDetail>> {

    if (this.network.lohntVersuch()) {
      try {
        const detail = await this.mitTimeout(
          this.api.updateWerte(messstelle.id, werte),
          InbetriebnahmeDataService.SCHREIB_TIMEOUT_MS
        );

        this.network.meldeErfolg();
        await this.aktualisiereLokalesDetail(detail);

        return { daten: detail, lokal: false };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    const kopf = await this.db.ladeListe(messstelle.messstellenListeId);

    const ergebnis = await this.lokalSchreiben(
      messstelle,
      'MESSSTELLE_WERTE',
      {
        entityId: messstelle.id,
        clientEntityId: null,
        baseVersion: messstelle.version ?? null,
        payload: { werte }
      },
      detail => {
        /*
         * Dieselbe Zuordnung Standardfeld -> Excel-Spalte wie serverseitig.
         * Käme hier eine andere Spalte heraus, stünde der Wert nach dem
         * Abgleich an zwei verschiedenen Stellen.
         */
        const columns = [...(kopf?.liste.columns ?? messstelle.columns ?? [])];
        const mapping = { ...(kopf?.liste.fieldMapping ?? messstelle.fieldMapping ?? {}) };

        // Welche Standardfelder gelten, haengt am Listentyp - genau wie
        // serverseitig in MessstelleWerteAnwender.
        const erlaubt = standardFelder(
          kopf?.liste.listenTyp ?? messstelle.listenTyp
        );

        /*
         * Manuell erstellte Liste: der Schlüssel ist die stabile Spalten-Id
         * (oder der Spaltenname). Es entsteht nie eine Spalte außerhalb der
         * Struktur – genau wie serverseitig.
         */
        const strukturSpalten = (kopf?.liste.struktur ?? messstelle.struktur)?.spalten ?? [];

        const neueEintraege: MessstellePruefprotokoll[] = [];

        for (const [feld, roh] of Object.entries(werte)) {

          const wert = (roh ?? '').trim();

          let spalte: string | null;

          if (strukturSpalten.length > 0) {
            const definition =
              strukturSpalten.find(s => s.id === feld)
              ?? strukturSpalten.find(s => s.name.trim().toLowerCase() === feld.trim().toLowerCase())
              ?? (mapping[feld] ? strukturSpalten.find(s => s.name === mapping[feld]) : undefined);

            if (!definition) {
              continue;
            }

            spalte = definition.name;
          } else {
            if (!erlaubt.includes(feld)) {
              continue;
            }

            spalte = findeSpalte(feld, mapping, columns);

            if (!spalte) {
              if (!wert) {
                continue;
              }

              spalte = eindeutigerSpaltenName(feld, columns);
              columns.push(spalte);
              mapping[feld] = spalte;
            }
          }

          const alt = detail.values[spalte] ?? '';

          if (alt === wert) {
            continue;
          }

          if (wert) {
            detail.values[spalte] = wert;
          } else {
            delete detail.values[spalte];
          }

          /*
           * Je Feld ein Verlaufseintrag – dieselbe Auflösung wie serverseitig.
           * Ein "allgemeine Daten geändert" beantwortete nicht, WER den
           * Messbereich von 100 auf 120 gesetzt hat.
           */
          neueEintraege.push(
            this.lokalerWertEintrag(
              detail.id,
              PruefprotokollTyp.AllgemeineDaten,
              {
                // Bei manuellen Listen steht im Verlauf der Spaltenname, nicht die Id.
                feldName: strukturSpalten.length > 0 ? spalte : feld,
                altWert: alt,
                neuWert: wert,
                paar: true
              }
            )
          );
        }

        // Neueste zuerst, wie im ganzen Verlauf.
        detail.pruefprotokoll = [
          ...neueEintraege.reverse(),
          ...(detail.pruefprotokoll ?? [])
        ];

        return undefined as any;
      }
    );

    return {
      daten: await this.lokalesDetailAlsMessstelle(messstelle.id, messstelle),
      lokal: ergebnis.lokal
    };
  }

  // ===================================================================
  // LOKALES SCHREIBEN
  // ===================================================================

  /**
   * Ändert den lokalen Bestand UND stellt die Änderung in die Warteschlange –
   * beides in EINER Dexie-Transaktion.
   *
   * Die Atomarität ist der Kern: bräche der Vorgang zwischen beidem ab,
   * stünde entweder eine Prüfung in der Oberfläche, die nie zum Server geht,
   * oder eine Änderung in der Warteschlange, die der Monteur nirgends sieht.
   * Beides wäre schlimmer als ein sauberer Fehler.
   */
  private async lokalSchreiben<T>(
    messstelle: MessstelleDetail,
    typ: SyncTyp,
    optionen: {
      clientId?: string;
      entityId: number | null;
      clientEntityId: string | null;
      vorlageId?: number | null;
      baseVersion: number | null;
      payload: any;
    },
    aendere: (detail: OfflineMessstelleDetail) => T
  ): Promise<SchreibErgebnis<T>> {

    if (!(await this.db.istVerfuegbar())) {
      throw new Error(
        'Ohne Verbindung und ohne lokalen Speicher kann nicht gespeichert werden.'
      );
    }

    const jetzt = new Date().toISOString();
    const eingereiht = this.naechsterReihenfolgeStempel();
    const db = this.db.db;

    let ergebnis!: T;
    let protokoll: MessstellePruefprotokoll[] = [];

    await db.transaction('rw', db.details, db.messstellen, db.syncQueue, async () => {
      const eintrag = await db.details.get(messstelle.id);

      if (!eintrag) {
        throw new Error(
          'Diese Messstelle steht offline nicht zur Verfügung. '
          + 'Die Inbetriebnahme muss vorher offline verfügbar gemacht werden.'
        );
      }

      ergebnis = aendere(eintrag.data);
      protokoll = eintrag.data.pruefprotokoll ?? [];

      await db.details.put(eintrag);

      // Die Listenansicht liest ihre Zähler und Chips aus einer eigenen
      // Tabelle. Ohne diesen Schritt zeigte die Übersicht nach einer
      // Offline-Prüfung noch den alten Fortschritt.
      await this.spiegeleInListenzeile(eintrag);

      /*
       * Gibt es für denselben Datensatz schon eine wartende Änderung?
       *
       * Dann wird sie ERSETZT statt eine zweite anzulegen. Das ist keine
       * Sparmaßnahme, sondern notwendig: der Änderungsstand `baseVersion`
       * stammt aus dem Download und wird lokal nicht hochgezählt. Zwei
       * Einträge trügen also denselben Stand – der erste ginge durch, der
       * Server stünde danach eine Version weiter, und der zweite käme als
       * Konflikt zurück. Der Benutzer geriete in Streit mit sich selbst.
       *
       * Der zuletzt erfasste Wert gewinnt, und genau das ist fachlich
       * richtig: er ist das, was der Monteur zuletzt gesehen hat.
       */
      const vorhanden = await this.findeWartendeAenderung(
        typ,
        messstelle.id,
        optionen
      );

      const warteschlange: SyncQueueEintrag = {
        // Kennung und Einreihung der bestehenden Änderung übernehmen: die
        // Kennung trägt die Idempotenz, die Einreihung die Reihenfolge.
        id: vorhanden?.id ?? optionen.clientId ?? neueUuid(),
        typ,
        entityId: optionen.entityId ?? vorhanden?.entityId ?? null,
        clientEntityId: optionen.clientEntityId ?? vorhanden?.clientEntityId ?? null,
        vorlageId: optionen.vorlageId ?? vorhanden?.vorlageId ?? null,
        messstellenListeId: messstelle.messstellenListeId,
        messstelleId: messstelle.id,

        // Der tatsächliche Prüfzeitpunkt – bei einer Korrektur der Zeitpunkt
        // der Korrektur. Er wandert bis ins Prüfprotokoll; dort soll 10:31
        // stehen, wenn um 10:31 geprüft wurde, und nicht 13:02, wenn erst
        // dann Netz war.
        performedAtUtc: jetzt,

        /*
         * Die Abarbeitungsreihenfolge. Bewusst NICHT derselbe Wert wie oben:
         * werden zwei Änderungen in derselben Millisekunde erfasst – etwa
         * „Prüfpunkt anlegen" und gleich darauf sein Ergebnis –, wäre die
         * Reihenfolge sonst zufällig, und das Ergebnis käme vor dem Punkt,
         * zu dem es gehört.
         */
        createdAtUtc: vorhanden?.createdAtUtc ?? eingereiht,

        // Der Stand, auf dem die ERSTE dieser Änderungen beruhte. Er ist der
        // Stand, den der Server noch hat.
        baseVersion: vorhanden
          ? vorhanden.baseVersion ?? null
          : optionen.baseVersion,

        payload: this.fasseNutzdatenZusammen(typ, vorhanden, optionen.payload),
        retryCount: 0
      };

      await db.syncQueue.put(warteschlange);
    });

    await this.sync.aktualisiereStatus();

    // Falls doch gerade eine Verbindung besteht, sofort versuchen. Schadet
    // nichts: läuft schon ein Abgleich, wird der Anstoß nur vorgemerkt.
    if (this.network.lohntVersuch()) {
      void this.sync.synchronisiere('aenderung');
    }

    return { daten: ergebnis, lokal: true, pruefprotokoll: protokoll };
  }

  /**
   * Sucht eine noch nicht gesendete Änderung derselben Art am selben
   * Datensatz.
   *
   * Endgültig abgelehnte Einträge zählen NICHT dazu: sie gehen ohnehin nicht
   * mehr hinaus, und sie zu überschreiben würde die Meldung des Servers
   * verschlucken, bevor der Benutzer sie gesehen hat.
   */
  private async findeWartendeAenderung(
    typ: SyncTyp,
    messstelleId: number,
    optionen: {
      entityId: number | null;
      clientEntityId: string | null;
    }
  ): Promise<SyncQueueEintrag | undefined> {

    /*
     * Anlegen und Löschen werden NIE zusammengefasst: jeder Aufruf meint
     * einen anderen Prüfpunkt. Zwei nacheinander ergänzte Punkte zu einem
     * zusammenzuziehen würde einen davon verschlucken.
     */
    if (typ === 'PRUEFPUNKT_ANLEGEN' || typ === 'PRUEFPUNKT_LOESCHEN') {
      return undefined;
    }

    const kandidaten = await this.db.db.syncQueue
      .filter(e =>
        e.typ === typ
        && e.messstelleId === messstelleId
        && !e.endgueltigFehlgeschlagen
      )
      .toArray();

    return kandidaten.find(e => {

      // Änderungen an der Messstelle selbst betreffen immer denselben
      // Datensatz – die Messstelle ist schon über messstelleId bestimmt.
      if (typ !== 'PRUEFPUNKT_ERGEBNIS') {
        return true;
      }

      // Beim Prüfpunkt muss zusätzlich der Punkt übereinstimmen.
      if (optionen.entityId != null) {
        return e.entityId === optionen.entityId;
      }

      return !!optionen.clientEntityId
        && e.clientEntityId === optionen.clientEntityId;
    });
  }

  /**
   * Führt die Nutzdaten einer ersetzten Änderung mit den neuen zusammen.
   *
   * Bei den allgemeinen Daten wird GEMISCHT: wer erst „Hersteller" und dann
   * „Typ" bearbeitet, hat beides geändert – der zweite Vorgang darf den
   * ersten nicht verwerfen. Bei allen anderen Arten trägt die Nutzlast den
   * vollständigen Zustand, dort gewinnt schlicht der neuere.
   */
  private fasseNutzdatenZusammen(
    typ: SyncTyp,
    vorhanden: SyncQueueEintrag | undefined,
    neu: any
  ): any {

    if (!vorhanden || typ !== 'MESSSTELLE_WERTE') {
      return neu;
    }

    return {
      werte: {
        ...(vorhanden.payload?.werte ?? {}),
        ...(neu?.werte ?? {})
      }
    };
  }

  /**
   * Änderung nur am lokalen Bestand, ohne Warteschlangeneintrag.
   *
   * Reine Nebenwirkung: „halte die lokale Kopie im Gleichschritt". Der
   * fachliche Vorgang – die Serverantwort – ist an dieser Stelle schon
   * erfolgreich gewesen. Ein Fehler beim Nachziehen darf ihn deshalb NICHT
   * mitreißen; im schlimmsten Fall ist die lokale Kopie einen Stand alt und
   * wird beim nächsten „Offline-Daten aktualisieren" gerichtet.
   *
   * Anders als `lokalSchreiben`, das den Vorgang SELBST speichert und daher
   * scheitern darf und muss.
   */
  private async aendereLokalesDetail<T>(
    messstelleId: number,
    aendere: (detail: OfflineMessstelleDetail) => T
  ): Promise<void> {
    if (!(await this.db.istVerfuegbar())) {
      return;
    }

    const db = this.db.db;

    try {
      await db.transaction('rw', db.details, db.messstellen, async () => {
        const eintrag = await db.details.get(messstelleId);

        if (!eintrag) {
          return;
        }

        aendere(eintrag.data);

        await db.details.put(eintrag);
        await this.spiegeleInListenzeile(eintrag);
      });
    } catch (fehler) {
      console.error(
        'Der lokale Bestand der Messstelle konnte nicht nachgezogen werden. '
        + 'Die Anzeige stammt vom Server und ist korrekt.',
        fehler
      );
    }
  }

  /**
   * Hält die Zeile der Listenansicht mit den Detaildaten in Übereinstimmung:
   * Fortschritt, Signalstatus, Prüfergebnis und Kategorie.
   */
  private async spiegeleInListenzeile(eintrag: OfflineDetailEintrag): Promise<void> {
    const zeile = await this.db.db.messstellen.get(eintrag.id);

    if (!zeile) {
      return;
    }

    const punkte = eintrag.data.pruefpunkte ?? [];

    zeile.data = {
      ...zeile.data,
      values: eintrag.data.values,
      kategorieId: eintrag.data.kategorieId ?? null,
      kategorieName: eintrag.data.kategorieName ?? null,
      pruefpunkteGesamt: punkte.length,
      pruefpunkteErledigt: punkte.filter(
        p => normalizePruefpunktErgebnis(p.ergebnis) !== PruefpunktErgebnis.NichtGeprueft
      ).length,
      signalStatus: eintrag.data.signalStatus,
      pruefergebnis: eintrag.data.pruefergebnis,
      version: eintrag.data.version
    };

    await this.db.db.messstellen.put(zeile);
  }

  /** Übernimmt eine Serverantwort in den lokalen Bestand. */
  private async aktualisiereLokalesDetail(detail: MessstelleDetail): Promise<void> {
    await this.aendereLokalesDetail(detail.id, lokal => {
      lokal.values = detail.values ?? {};
      lokal.kategorieId = detail.kategorieId ?? null;
      lokal.kategorieName = detail.kategorieName ?? null;
      lokal.anlagenteilId = detail.anlagenteilId ?? null;
      lokal.anlagenteilName = detail.anlagenteilName ?? null;
      lokal.pruefergebnis = detail.pruefergebnis;
      lokal.pruefergebnisBemerkung = detail.pruefergebnisBemerkung ?? null;
      lokal.signalStatus = detail.signalStatus;
      lokal.version = detail.version ?? lokal.version;
      lokal.updatedAtUtc = detail.updatedAtUtc ?? lokal.updatedAtUtc;
      lokal.pruefpunkte = detail.pruefpunkte ?? [];
      lokal.pruefprotokoll = detail.pruefprotokoll ?? [];
      lokal.bilder = detail.bilder ?? [];

      return undefined;
    });
  }

  private async uebernehmeServerPunkt(
    messstelleId: number,
    punkt: MessstellePruefpunkt
  ): Promise<void> {
    await this.aendereLokalesDetail(messstelleId, detail => {
      detail.pruefpunkte = (detail.pruefpunkte ?? []).map(p =>
        p.id === punkt.id ? { ...punkt, nichtSynchronisiert: false } : p
      );

      return undefined;
    });
  }

  /** Übernimmt eine Serverantwort für die ganze Liste in den lokalen Bestand. */
  private async aktualisiereLokaleListe(
    listeId: number,
    liste: MessstellenListeDetail
  ): Promise<void> {
    const db = this.db.db;

    /*
     * `syncQueue` gehört zwingend in den Geltungsbereich, obwohl hier nur
     * daraus GELESEN wird: Dexie lässt innerhalb einer Transaktion
     * ausschliesslich die aufgezählten Tabellen zu und wirft sonst
     * "The specified object store was not found".
     *
     * Gelesen wird die Warteschlange bewusst INNERHALB der Transaktion – die
     * Prüfung „stehen noch Änderungen aus?" und das Überschreiben der Zeilen
     * müssen zusammen entschieden werden. Läge die Prüfung davor, könnte
     * genau dazwischen eine Prüfung erfasst werden, die der Download danach
     * überschreibt.
     */
    try {
      await db.transaction('rw', db.listen, db.messstellen, db.syncQueue, async () => {
        const kopf = await db.listen.get(listeId);

        if (!kopf) {
          return;
        }

        const { messstellen, ...summary } = liste as any;

        kopf.liste = summary;
        kopf.name = liste.name;
        kopf.messstellenCount = liste.messstellen?.length ?? 0;

        await db.listen.put(kopf);

        /*
         * Bewusst nur die Zeilen und NICHT die Detaildatensätze: hier könnten
         * noch nicht synchronisierte Prüfungen liegen, und die dürfen von
         * einem Serverstand nicht überschrieben werden. Der ausdrückliche
         * "Offline-Daten aktualisieren" macht das – nachdem alles Ausstehende
         * angekommen ist.
         */
        const ausstehend = await db.syncQueue
          .where('messstellenListeId')
          .equals(listeId)
          .count();

        if (ausstehend > 0) {
          return;
        }

        await db.messstellen.bulkPut(
          (liste.messstellen ?? []).map(m => ({
            id: m.id,
            messstellenListeId: listeId,
            rowNumber: m.rowNumber,
            data: m
          }))
        );

        /*
         * Was der Server nicht mehr führt, wurde dort gelöscht (etwa eine
         * nachträglich entfernte Messstelle). Ohne diesen Schritt tauchte sie
         * offline wieder auf. Keine ausstehenden Änderungen – siehe oben –,
         * es geht also nichts verloren.
         */
        const aufDemServer = new Set((liste.messstellen ?? []).map(m => m.id));

        const veraltet = (await db.messstellen
          .where('messstellenListeId')
          .equals(listeId)
          .primaryKeys())
          .filter(id => !aufDemServer.has(id as number));

        if (veraltet.length > 0) {
          await db.messstellen.bulkDelete(veraltet);
        }
      });
    } catch (fehler) {
      // Nebenwirkung, siehe aendereLokalesDetail: die angezeigte Liste kommt
      // vom Server und ist korrekt. Ein Fehler beim Nachziehen der lokalen
      // Kopie darf die Seite nicht scheitern lassen.
      console.error(
        'Der lokale Bestand der Inbetriebnahme konnte nicht nachgezogen werden. '
        + 'Die Anzeige stammt vom Server und ist korrekt.',
        fehler
      );
    }
  }

  // ===================================================================
  // UMFORMEN
  // ===================================================================

  /**
   * Setzt einen lokalen Detaildatensatz mit den Angaben der Liste zu einer
   * vollständigen Messstelle zusammen.
   *
   * Spalten, Feldzuordnung, Kunde und Projekt stehen bewusst nur einmal am
   * Listenkopf: sie 378-mal zu speichern wäre reine Verschwendung.
   */
  private hydriere(
    detail: OfflineMessstelleDetail,
    kopf: OfflineListeEintrag | undefined
  ): MessstelleDetail {
    return {
      id: detail.id,
      rowNumber: detail.rowNumber,
      messstellenListeId: kopf?.id ?? 0,
      messstellenListeName: kopf?.name ?? '',
      customerName: kopf?.customerName ?? null,
      projektId: kopf?.projektId ?? null,
      projektName: kopf?.projektName ?? null,
      projektNummer: kopf?.projektNummer ?? null,
      columns: kopf?.liste.columns ?? [],
      values: detail.values ?? {},
      fieldMapping: kopf?.liste.fieldMapping ?? {},
      columnClassifications: kopf?.columnClassifications ?? null,

      /*
       * Listentyp und Kennzeichnungsart stehen am Listenkopf des Pakets.
       * Ohne sie zeigte die Detailseite vor Ort wieder Messstellenfelder
       * über einer Verbraucherliste und pauschal "Kennzeichnung" statt
       * "SKS". Ein älteres Paket kennt beides noch nicht – dann greifen
       * dieselben Vorgaben wie bei jedem Altbestand.
       */
      listenTyp: kopf?.liste.listenTyp ?? null,
      kennzeichnungsArt: kopf?.liste.kennzeichnungsArt ?? null,
      kennzeichnungLabel: kopf?.liste.kennzeichnungLabel ?? null,

      // Manuell erstellte Liste: die Detailseite baut ihr Formular aus der Struktur.
      struktur: kopf?.liste.struktur ?? null,

      kategorieId: detail.kategorieId ?? null,
      kategorieName: detail.kategorieName ?? null,

      anlagenteilId: detail.anlagenteilId ?? null,
      anlagenteilName: detail.anlagenteilName ?? null,

      /*
       * Die Auswahl steht offline zwar bereit, ist aber gesperrt: für den
       * Anlagenteil gibt es keinen Offline-Änderungstyp, und eine still
       * verworfene Änderung wäre schlimmer als eine gesperrte Auswahl.
       */
      anlagenteile: kopf?.liste.anlagenteile ?? [],
      pruefergebnis: normalizePruefergebnis(detail.pruefergebnis),
      pruefergebnisBemerkung: detail.pruefergebnisBemerkung ?? null,
      signalStatus: normalizeSignalStatus(detail.signalStatus),
      pruefpunkte: detail.pruefpunkte ?? [],
      pruefprotokoll: detail.pruefprotokoll ?? [],
      bilder: detail.bilder ?? [],

      // Offline gilt der Stand vom Download. Wer beim Herunterladen nicht
      // bearbeiten durfte, darf es vor Ort auch nicht.
      canEdit: kopf?.canEdit ?? false,

      version: detail.version,
      updatedAtUtc: detail.updatedAtUtc ?? null
    };
  }

  private async lokalesDetailAlsMessstelle(
    messstelleId: number,
    ersatz: MessstelleDetail
  ): Promise<MessstelleDetail> {
    const eintrag = await this.db.ladeDetail(messstelleId);

    if (!eintrag) {
      return ersatz;
    }

    const kopf = await this.db.ladeListe(eintrag.messstellenListeId);

    return this.hydriere(eintrag.data, kopf);
  }

  private alsKategorie(k: OfflineKategorie): MessstelleKategorie {
    return {
      id: k.id,
      name: k.name,
      beschreibung: k.beschreibung ?? null,
      color: k.color ?? null,
      sortOrder: k.sortOrder,
      isActive: k.isActive,
      vorlagenCount: k.vorlagenCount,
      // Struktur mitgeben – die Kategorieauswahl zeigt daraus offline dieselbe
      // Vorschau der Abschnitte und Prüfpunkte wie online.
      abschnitte: k.abschnitte ?? [],
      vorlagenOhneAbschnitt: k.vorlagenOhneAbschnitt ?? []
    };
  }

  /**
   * Erzeugt die Prüfpunkte einer Kategorie lokal – mit derselben
   * Aufräumlogik wie der Server: unberührte Vorlagenpunkte der alten
   * Kategorie verschwinden, bearbeitete und von Hand ergänzte bleiben.
   */
  private baueVorlagenPunkte(
    vorhanden: MessstellePruefpunkt[],
    kategorie: OfflineKategorie | null
  ): MessstellePruefpunkt[] {

    const vorlagen: { vorlage: any; abschnitt: string }[] = [];

    for (const abschnitt of kategorie?.abschnitte ?? []) {
      for (const v of abschnitt.vorlagen) {
        vorlagen.push({ vorlage: v, abschnitt: abschnitt.name || 'Allgemein' });
      }
    }

    for (const v of kategorie?.vorlagenOhneAbschnitt ?? []) {
      vorlagen.push({ vorlage: v, abschnitt: 'Allgemein' });
    }

    const zielIds = new Set(vorlagen.map(v => v.vorlage.id));

    const bleibt = vorhanden.filter(p => {
      // Von Hand ergänzte Punkte überleben immer.
      if (p.vorlageId == null) {
        return true;
      }

      if (zielIds.has(p.vorlageId)) {
        return true;
      }

      // Bearbeitete Punkte ebenfalls – hier steckt erfasste Arbeit drin.
      return this.istBearbeitet(p);
    });

    const vorhandeneVorlagen = new Set(
      bleibt.filter(p => p.vorlageId != null).map(p => p.vorlageId!)
    );

    const neue: MessstellePruefpunkt[] = [];

    for (const { vorlage, abschnitt } of vorlagen) {
      if (vorhandeneVorlagen.has(vorlage.id)) {
        continue;
      }

      vorhandeneVorlagen.add(vorlage.id);

      neue.push({
        /*
         * Diese Punkte entstehen beim Abgleich AUF DEM SERVER neu, aus
         * derselben Kategorie. Sie gehen deshalb nicht einzeln in die
         * Warteschlange – die Kategoriezuweisung genügt. Bis zum nächsten
         * Download tragen sie eine lokale Kennung.
         */
        id: neueLokaleId(),
        vorlageId: vorlage.id,
        section: abschnitt,
        bezeichnung: vorlage.bezeichnung,
        sortOrder: vorlage.sortOrder,
        ergebnis: PruefpunktErgebnis.NichtGeprueft,
        status: PruefpunktStatus.Offen,
        bewertung: PruefpunktBewertung.Keine,
        bemerkung: null,
        quittiertAmUtc: null,
        quittiertVon: null,
        version: 0,
        nichtSynchronisiert: true
      });
    }

    return [...bleibt, ...neue].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
    );
  }

  /** Spiegelt `MessstellePruefpunkt.IstBearbeitet` im Backend. */
  private istBearbeitet(p: MessstellePruefpunkt): boolean {
    return (
      normalizePruefpunktErgebnis(p.ergebnis) !== PruefpunktErgebnis.NichtGeprueft
      || !!p.quittiertAmUtc
      || !!(p.bemerkung && p.bemerkung.trim())
    );
  }

  private setzePunktErgebnis(
    punkt: MessstellePruefpunkt,
    ergebnis: PruefpunktErgebnis,
    bemerkung: string | null
  ): void {
    punkt.ergebnis = ergebnis;
    punkt.bemerkung = bemerkung;

    // Die Altbestandsfelder mitziehen, genau wie
    // MessstellePruefpunkt.UebernehmeErgebnis serverseitig.
    punkt.status =
      ergebnis === PruefpunktErgebnis.Bestanden
        || ergebnis === PruefpunktErgebnis.NichtBestanden
        ? PruefpunktStatus.Erledigt
        : ergebnis === PruefpunktErgebnis.Irrelevant
          ? PruefpunktStatus.Irrelevant
          : PruefpunktStatus.Offen;

    punkt.bewertung =
      ergebnis === PruefpunktErgebnis.Bestanden
        ? PruefpunktBewertung.InOrdnung
        : ergebnis === PruefpunktErgebnis.NichtBestanden
          ? PruefpunktBewertung.NichtInOrdnung
          : PruefpunktBewertung.Keine;

    if (ergebnis === PruefpunktErgebnis.NichtGeprueft) {
      punkt.quittiertAmUtc = null;
      punkt.quittiertVon = null;
    } else {
      punkt.quittiertAmUtc = new Date().toISOString();
      punkt.quittiertVon = this.auth.getAnzeigenameAusToken();
    }
  }

  /**
   * Ein Protokolleintrag für eine offline erfasste Änderung.
   *
   * Nur für die Anzeige: der maßgebliche Eintrag entsteht beim Abgleich auf
   * dem Server, mit dem Benutzer aus dem Token. Der Name hier stammt aus dem
   * lokalen Token und wird nie zum Server geschickt – sonst könnte ein
   * manipulierter Client einen fremden Namen ins Protokoll schreiben.
   */
  private lokalerProtokolleintrag(
    messstelleId: number,
    typ: PruefprotokollTyp,
    ergebnis: number,
    vorherigesErgebnis: number | null,
    bemerkung: string | null,
    pruefpunktId: PruefpunktId | null = null,
    pruefpunktBezeichnung: string | null = null
  ): MessstellePruefprotokoll {
    const text = (wert: number): string => {
      switch (typ) {
        case PruefprotokollTyp.MessstellenErgebnis:
          return pruefergebnisText(wert);

        case PruefprotokollTyp.SignalStatus:
          return signalStatusText(wert);

        default:
          return pruefpunktErgebnisText(wert);
      }
    };

    return {
      ...this.protokollGeruest(messstelleId, typ),
      pruefpunktId,
      pruefpunktBezeichnung,
      ergebnis,
      vorherigesErgebnis,
      ergebnisText: text(ergebnis),
      vorherigesErgebnisText:
        vorherigesErgebnis != null ? text(vorherigesErgebnis) : null,
      bemerkung
    };
  }

  /**
   * Ein Verlaufseintrag für eine WERT-Änderung: geänderte allgemeine Daten,
   * ein ergänzter Prüfpunkt, eine gewechselte Zuordnung.
   *
   * `ergebnisText`/`vorherigesErgebnisText` werden hier gefüllt, weil die
   * Anzeige nur diese beiden Felder liest – online kommen sie fertig
   * beschriftet vom Server, offline muss dasselbe herauskommen. Leere Werte
   * werden dabei sichtbar gemacht, sonst stünde im Verlauf ein Pfeil ins
   * Nichts.
   */
  private lokalerWertEintrag(
    messstelleId: number,
    typ: PruefprotokollTyp,
    optionen: {
      feldName?: string | null;
      altWert?: string | null;
      neuWert?: string | null;
      pruefpunktId?: PruefpunktId | null;
      pruefpunktBezeichnung?: string | null;
      beschreibung?: string | null;
      bemerkung?: string | null;

      /** Trägt der Eintrag ein Paar "vorher → nachher"? */
      paar?: boolean;
    }
  ): MessstellePruefprotokoll {

    const leer =
      typ === PruefprotokollTyp.Kategorie
        || typ === PruefprotokollTyp.Anlagenteil
        ? InbetriebnahmeDataService.KEINE_ZUORDNUNG
        : InbetriebnahmeDataService.LEER_TEXT;

    const anzeige = (wert: string | null | undefined): string =>
      (wert ?? '').trim() ? (wert as string) : leer;

    return {
      ...this.protokollGeruest(messstelleId, typ),
      pruefpunktId: optionen.pruefpunktId ?? null,
      pruefpunktBezeichnung: optionen.pruefpunktBezeichnung ?? null,
      ergebnis: 0,
      vorherigesErgebnis: null,

      ergebnisText:
        optionen.paar
          ? anzeige(optionen.neuWert)
          : (optionen.neuWert ?? ''),

      vorherigesErgebnisText:
        optionen.paar ? anzeige(optionen.altWert) : null,

      bemerkung: optionen.bemerkung ?? null,
      feldName: optionen.feldName ?? null,
      altWert: optionen.altWert ?? null,
      neuWert: optionen.neuWert ?? null,
      beschreibung: optionen.beschreibung ?? null
    };
  }

  /** Was jeder lokale Verlaufseintrag gemeinsam hat. */
  private protokollGeruest(
    messstelleId: number,
    typ: PruefprotokollTyp
  ): MessstellePruefprotokoll {
    return {
      id: neueLokaleId(),
      messstelleId,
      pruefpunktId: null,
      pruefpunktBezeichnung: null,
      benutzerId: null,
      benutzerName: this.auth.getAnzeigenameAusToken(),

      // Der Zeitpunkt VOR ORT. Beim Abgleich meldet ihn der Client mit, und
      // der maßgebliche Eintrag des Servers trägt denselben – nicht den der
      // Übertragung.
      zeitpunktUtc: new Date().toISOString(),

      typ,
      gruppe: aktivitaetsGruppeVon(typ),
      ergebnis: 0,
      vorherigesErgebnis: null,
      ergebnisText: '',
      vorherigesErgebnisText: null,
      bemerkung: null,
      nichtSynchronisiert: true
    };
  }

  /**
   * Der Stand eines Prüfpunkts beim Löschen – dieselbe Zeile wie
   * serverseitig. Einen bereits quittierten Punkt zu entfernen ist etwas
   * anderes, als einen unbearbeiteten wegzuräumen.
   */
  private loeschHinweis(punkt: MessstellePruefpunkt): string {
    const teile: string[] = [];

    if (punkt.section) {
      teile.push(`Bereich: ${punkt.section}`);
    }

    teile.push(
      `Stand: ${pruefpunktErgebnisText(
        normalizePruefpunktErgebnis(punkt.ergebnis)
      )}`
    );

    return teile.join(' · ');
  }

  private clientKennung(punkt: MessstellePruefpunkt): string {
    if (punkt.clientId) {
      return punkt.clientId;
    }

    return typeof punkt.id === 'string' && punkt.id.startsWith('local:')
      ? punkt.id.substring('local:'.length)
      : neueUuid();
  }

  // ===================================================================
  // HILFSMITTEL
  // ===================================================================

  /**
   * Führt eine Anfrage mit Zeitgrenze aus.
   *
   * Ohne die bliebe die Oberfläche auf einer Anlage minutenlang stehen: eine
   * TCP-Verbindung, die niemand annimmt, läuft erst nach der Zeitgrenze des
   * Betriebssystems aus. Der Timeout ist damit der HÄUFIGSTE Auslöser des
   * Offline-Fallbacks – nicht das `offline`-Ereignis des Browsers.
   */
  private mitTimeout<T>(quelle: Observable<T>, ms: number): Promise<T> {
    return firstValueFrom(
      quelle.pipe(
        timeout({
          each: ms,
          // Muss ein Observable liefern - ein synchrones throw im Fabrikaufruf
          // waere ein anderer Fehlerweg und ginge an istNetzwerkfehler vorbei.
          with: () => throwError(() => new TimeoutFehler(ms))
        })
      )
    );
  }

  /**
   * Zuletzt vergebener Reihenfolgestempel. Sorgt dafür, dass die
   * Warteschlange eine echte Reihenfolge hat.
   */
  private letzterStempelMs = 0;

  /**
   * Ein streng aufsteigender Zeitstempel für die Warteschlange.
   *
   * `Date.now()` allein genügt nicht: zwei Änderungen in derselben
   * Millisekunde bekämen denselben Schlüssel, und Dexie sortierte sie dann
   * nach der zufälligen UUID. Ein „Prüfpunkt anlegen" könnte so NACH der
   * Änderung seines Ergebnisses beim Server ankommen – und die fände den
   * Punkt noch nicht.
   */
  private naechsterReihenfolgeStempel(): string {
    const jetzt = Date.now();

    this.letzterStempelMs =
      jetzt > this.letzterStempelMs ? jetzt : this.letzterStempelMs + 1;

    return new Date(this.letzterStempelMs).toISOString();
  }

  /** Merkt sich Netzwerkfehler für die Entscheidung, ob ein Versuch lohnt. */
  private behandleFehler(fehler: unknown): void {
    if (NetworkStatusService.istNetzwerkfehler(fehler)) {
      this.network.meldeNetzwerkfehler();
    }
  }

  private juengsterStand(listen: OfflineListeEintrag[]): string | null {
    const staende = listen.map(l => l.downloadedAtUtc).filter(Boolean).sort();

    return staende.length > 0 ? staende[staende.length - 1] : null;
  }
}
